import type { Action } from 'app/scripts/models/action';
import type { Card } from 'app/scripts/models/card';
import type { List } from 'app/scripts/models/list';
import type { PendingOrganization } from 'app/scripts/models/PendingOrganization';
import type { Member } from 'app/scripts/models/member';
import type { Notification } from 'app/scripts/models/notification';
import type { NotificationGroup } from 'app/scripts/models/NotificationGroup';
import type { Plugin } from 'app/scripts/models/Plugin';
import type { Reaction } from 'app/scripts/models/reaction';
import type { PayloadQuery } from 'app/scripts/network/payloads';
import type { Board } from 'app/scripts/models/board';
import type { Enterprise } from 'app/scripts/models/enterprise';
import type { Organization } from 'app/scripts/models/organization';

import BluebirdPromise from 'bluebird';
import { QuickLoad } from '@trello/quickload';
import { Util } from 'app/scripts/lib/util';
import _ from 'underscore';
import { loadApiDataFromUrl } from './loadApiDataFromUrl';
import { ModelCache } from './model-cache';
import { ninvoke } from 'app/scripts/lib/util/ninvoke';
import { getInvitationTokens } from '@trello/invitation-tokens';

interface LoadApiDataOptions {
  id?: string | null;
  payload?: PayloadQuery;
  path?: string | null;
  metadataHeaderName?: string | null;
  isHeaderLoad?: boolean | null;
  traceId?: string | null;
}

export function loadApiDataFromPayload(
  modelType: 'Member',
  options: LoadApiDataOptions,
): BluebirdPromise<Member>;
export function loadApiDataFromPayload(
  modelType: 'Organization',
  options: LoadApiDataOptions,
): BluebirdPromise<Organization>;
export function loadApiDataFromPayload(
  modelType: 'Board',
  options: LoadApiDataOptions,
): BluebirdPromise<Board>;
export function loadApiDataFromPayload(
  modelType: 'Card',
  options: LoadApiDataOptions,
): BluebirdPromise<Card>;
export function loadApiDataFromPayload(
  modelType: 'Notification',
  options: LoadApiDataOptions,
): BluebirdPromise<Notification>;
export function loadApiDataFromPayload(
  modelType: 'NotificationGroup',
  options: LoadApiDataOptions,
): BluebirdPromise<NotificationGroup>;
export function loadApiDataFromPayload(
  modelType: 'Action',
  options: LoadApiDataOptions,
): BluebirdPromise<Action>;
export function loadApiDataFromPayload(
  modelType: 'Enterprise',
  options: LoadApiDataOptions,
): BluebirdPromise<Enterprise>;
export function loadApiDataFromPayload(
  modelType: 'List',
  options: LoadApiDataOptions,
): BluebirdPromise<List>;
export function loadApiDataFromPayload(
  modelType: 'Plugin',
  options: LoadApiDataOptions,
): BluebirdPromise<Plugin[]>;
export function loadApiDataFromPayload(
  modelType: 'PendingOrganization',
  options: LoadApiDataOptions,
): BluebirdPromise<PendingOrganization>;
export function loadApiDataFromPayload(
  modelType: 'Reaction',
  options: LoadApiDataOptions,
): BluebirdPromise<Reaction>;
export function loadApiDataFromPayload(
  modelType: 'notificationsCount' | 'highlights' | 'upNext' | 'search',
  options: LoadApiDataOptions,
): BluebirdPromise<unknown>;
export function loadApiDataFromPayload(
  modelType: string,
  {
    id,
    payload,
    path,
    metadataHeaderName,
    isHeaderLoad,
    traceId,
  }: LoadApiDataOptions,
): BluebirdPromise<unknown> {
  let { query } = payload || {};
  const { operationName, mappingRules = {} } = payload || {};

  // If we gave a class like Board, convert that to "Board"
  if (!_.isString(modelType)) {
    // We expect an error here because we have no way to tell the signature that this can be any Trello Model. I think
    // we should work towards converging on the `modelType: string` signature and then delete this code once it's safe.
    // @ts-expect-error
    modelType = modelType.prototype.typeName;
  }

  if (typeof path === 'undefined' || path === null) {
    path = `${Util.pluralize(modelType)}/${id}`;
  }

  const invitationTokens = getInvitationTokens();
  if (invitationTokens) {
    // Note that this has to match the behavior of quickload; where
    // invitationTokens is added as the last thing
    query = _.extend({}, query, { invitationTokens });
  }

  const url = QuickLoad.makeUrl(`/1/${path}`, query);

  return loadApiDataFromUrl(url, {
    isHeaderLoad,
    modelType,
    traceId,
    operationName,
  }).spread(function (data: JSON, xhr: XMLHttpRequest) {
    return BluebirdPromise.try(function () {
      if (data) {
        return ninvoke(
          ModelCache,
          'enqueueDelta',
          modelType,
          data,
          mappingRules,
          { query },
        );
      } else {
        return [];
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }).then(function (models: any) {
      if (!metadataHeaderName) {
        return models;
      }
      const metadata = JSON.parse(
        xhr.getResponseHeader(metadataHeaderName) || '{}',
      );
      return [models, metadata];
    });
  });
}
